import type { LocaleSelectorModalProps } from './LocaleSelectorModal';
import type { GlobalLocaleSelector } from '../../../../amplienceTypes/schemas/imported/global-locale-selector-schema';
import type { HeaderOpenModalProps } from '../../core/layouts/MasterLayout';
import type { HeaderItemPosition } from '../sideBar/SideNavHeader';
import type { ReactElement } from 'react';

import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { getLanguageName } from './getLanguageName';
import { LocaleSelectorIcon } from './LocaleSelectorIcon';
import { useLocaleSelectorAutoOpen } from './useLocaleSelectorAutoOpen';
import { useAppContext } from '../../../../utilities/context/static/AppContext';
import { useDataLayerContext } from '../../../../utilities/context/static/DataLayerContext';
import { useAvailableCountries } from '../../../../utilities/hooks/useAvailableCountries';
import { parseLocaleSelectorDataLayer } from '../../../../utilities/vendors';
import { media } from '../../core/styles';
import { HeaderButton } from '../HeaderStyle';
import { useMobileTopLevelNavigationOwnsOverlays } from '../topLevelNavigation/hooks/useTopLevelNavigationEnabled';

export interface LocaleSelectorButtonProps {
  buttonPosition?: HeaderItemPosition;
  onClick?: () => void;
}

const LocaleSelectorModal = dynamic<LocaleSelectorModalProps>(
  () => import('./LocaleSelectorModal').then(mod => mod.LocaleSelectorModal),
  { ssr: false }
);

const S = {
  LocaleSelectorButton: styled.button<{ $buttonPosition: HeaderItemPosition }>`
    ${({ theme, $buttonPosition }) => css`
      display: ${$buttonPosition === 'main-header' ? 'none' : 'flex'};
      ${HeaderButton}

      @media ${media(theme).greaterThan('sm')} {
        min-width: ${theme.size[5]};
        display: flex;
        gap: ${theme.size[1]};
        position: relative;
      }
    `}
  `,
};

export const LocaleSelectorButton = ({
  buttonPosition = 'main-header',
  title = '',
  btnClose = '',
  btnContinue = '',
  cartWarning = '',
  localeSelectorAriaLabel = '',
  openedModal,
  setOpenedModal,
  onClick,
}: LocaleSelectorButtonProps & GlobalLocaleSelector & HeaderOpenModalProps): ReactElement => {
  const { country, language } = useAppContext();
  // Below `lg` the mobile TLN drawer owns the locale selector. Neither copy of this button (top
  // navigation: hidden there; side navigation: its dialog is replaced by the drawer) auto-opens or
  // renders the modal, so only the drawer's copy exists. Nothing happens until the viewport is known.
  const ownsLocaleSelector = useMobileTopLevelNavigationOwnsOverlays() === false;
  const { suggestedCountry, suggestedLanguage, clearSuggestedLanguage } = useLocaleSelectorAutoOpen(
    setOpenedModal,
    ownsLocaleSelector
  );
  const { pushToDataLayer } = useDataLayerContext();
  const { supportedLanguages } = useAvailableCountries();

  const currentLanguageName = useMemo(
    () => getLanguageName(supportedLanguages, country, language),
    [supportedLanguages, country, language]
  );

  const ariaLabel = localeSelectorAriaLabel.replace('{language}', currentLanguageName);

  return (
    <>
      <S.LocaleSelectorButton
        type="button"
        $buttonPosition={buttonPosition}
        onClick={() => {
          clearSuggestedLanguage();
          setOpenedModal('locale-selector');
          pushToDataLayer(parseLocaleSelectorDataLayer('open'));
          onClick?.();
        }}
        aria-label={ariaLabel}
        aria-haspopup="true"
        data-cs-capture=""
      >
        <LocaleSelectorIcon language={language} />
      </S.LocaleSelectorButton>
      {openedModal === 'locale-selector' && ownsLocaleSelector && (
        <LocaleSelectorModal
          onClose={() => setOpenedModal(null)}
          country={suggestedCountry || country}
          language={suggestedLanguage || language}
          title={title}
          btnClose={btnClose}
          btnContinue={btnContinue}
          cartWarning={cartWarning}
          buttonPosition={buttonPosition}
        />
      )}
    </>
  );
};
